import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useCallback,
  useState
} from "react";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { Button, ButtonVariant, ButtonSize } from "shared/components/Button";
import { Stepper } from "../components/Stepper";
import { loadPropertyOptions, loadUnitOptions } from "../api";
import { Header } from "../components/Header";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { CriteriaRentType, PropertyKind } from "shared/types";
import { DiscountTag02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ActiveDiscountsModal } from "views/discounts/components/ActiveDiscountsModal";
import { useDiscounts } from "views/discounts/hooks/useDiscounts";
import {
  RentAmountData,
  SetRentAmountOptions
} from "../components/SetRentAmountOptions";
import { useAuthContext } from "providers/Auth";
import { Show } from "shared/components";
import api from "api/landlord/address";
import { TemplateSelector } from "./components/TemplateSelector";
import { Template } from "types/landlord/verificationTemplate";

export const ChooseProperty = ({
  setOpen,
  setSelectedPropertyId,
  setSelectedUnitId,
  selectedPropertyId,
  selectedUnitId,
  setStepIdx,
  propertyName,
  applicationId,
  unitName,
  manualRent,
  setManualRent,
  useListedRent,
  setUseListedRent,
  onTemplateChange
}: {
  setOpen: (open: boolean) => void;
  setSelectedPropertyId: Dispatch<SetStateAction<number | undefined>>;
  setSelectedUnitId: Dispatch<SetStateAction<number | undefined>>;
  selectedPropertyId: number | undefined;
  selectedUnitId: number | undefined;
  setStepIdx: Dispatch<SetStateAction<number>>;
  propertyName?: string;
  unitName?: string;
  applicationId?: number;
  manualRent?: RentAmountData;
  setManualRent: Dispatch<SetStateAction<RentAmountData | undefined>>;
  useListedRent: boolean;
  setUseListedRent: (useListedRent: boolean) => void;
  onTemplateChange: (template: Template | undefined) => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [showActiveDiscountCodes, setShowActiveDiscountCodes] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    Template | undefined
  >(undefined);

  // Fetch property data to get verification template ID
  const { data: propertyData, isLoading: isPropertyDataLoading } = useQuery({
    queryKey: ["PROPERTY", selectedPropertyId],
    queryFn: () =>
      selectedPropertyId
        ? addressApi.getProperty(String(selectedPropertyId))
        : null,
    enabled: Boolean(selectedPropertyId)
  });

  const { data: availableDiscounts = [] } = useDiscounts({
    active: true,
    restriction_type: "template",
    template_id: selectedTemplate?.id,
    enabled: false
  });

  const isSingleFamily = useMemo(
    () => propertyData?.kind === PropertyKind.SingleFamily,
    [propertyData?.kind]
  );

  const unitOptions = useMemo(() => {
    if (isSingleFamily && propertyData?.units?.[0])
      return [{ label: "Single family", value: propertyData.units[0].id }];

    if (unitName && selectedUnitId)
      return [{ label: unitName, value: selectedUnitId }];

    return [];
  }, [isSingleFamily, propertyData?.units, unitName, selectedUnitId]);

  useEffect(() => {
    if (isSingleFamily && propertyData?.units?.[0])
      setSelectedUnitId(propertyData.units[0].id);
  }, [isSingleFamily, propertyData?.units, setSelectedUnitId]);

  const unitValue = useMemo(() => {
    if (isSingleFamily && propertyData?.units?.[0])
      return propertyData.units[0].id;

    if (unitName && selectedUnitId) return selectedUnitId;

    return "";
  }, [isSingleFamily, propertyData?.units, unitName, selectedUnitId]);
  const { pms } = useAuthContext();
  const { data: unit, isLoading } = useQuery({
    queryKey: ["unit", selectedUnitId],
    queryFn: (query) => api.getUnitById(String(query.queryKey[1])),
    enabled: Boolean(selectedUnitId)
  });

  const handleTemplateChange = useCallback(
    (template: Template | undefined) => {
      if (!template) return;

      setSelectedTemplate(template);
      onTemplateChange(template);
    },
    [setSelectedTemplate, onTemplateChange]
  );

  const isDisabledSubmit = useMemo(() => {
    if (!selectedPropertyId || !selectedUnitId || !unit) return true;
    if (useListedRent) return false;
    if (pms.isManageAmerica) {
      if (
        unit.verification_template?.criteria_rent_type === CriteriaRentType.Site
      )
        return manualRent?.site_rent_amount?.cents === undefined;
      if (
        unit.verification_template?.criteria_rent_type === CriteriaRentType.Home
      )
        return manualRent?.home_rent_amount?.cents === undefined;
      return (
        manualRent?.home_rent_amount?.cents === undefined &&
        manualRent?.site_rent_amount?.cents === undefined
      );
    }
    return manualRent?.rent_amount?.cents === undefined;
  }, [
    selectedPropertyId,
    selectedUnitId,
    unit,
    useListedRent,
    pms.isManageAmerica,
    manualRent
  ]);

  useEffect(() => {
    if (selectedUnitId) setManualRent(undefined);
  }, [selectedUnitId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <Header
        title={`Select ${dictionary.unit}`}
        description={`Select a ${dictionary.unit} for which you want to create an application`}
        onClose={() => setOpen(false)}
      />

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-6 border-b border-alpha-40">
          <Stepper currentStepIdx={0} totalSteps={2} />
        </div>

        <div className="space-y-6 p-6">
          <div className="flex gap-4">
            <div className="w-full">
              <AsyncSelect
                id="property"
                disabled={Boolean(propertyName)}
                label={dictionary.Property}
                value={selectedPropertyId || ""}
                menuPortalTarget={document.body}
                searchable={true}
                menuPosition="fixed"
                menuPlacement="auto"
                hideOptional
                // @ts-expect-error
                loadOptions={loadPropertyOptions}
                options={
                  propertyName && selectedPropertyId
                    ? [
                        {
                          label: propertyName,
                          value: selectedPropertyId
                        }
                      ]
                    : []
                }
                onChange={(e) => {
                  if (!Array.isArray(e)) {
                    setSelectedPropertyId(Number(e.target.value));
                    setSelectedUnitId(undefined);
                  }
                }}
                additional={{
                  page: 1
                }}
              />
            </div>
            <div className="w-full">
              <AsyncSelect
                id="unit"
                label={dictionary.Unit}
                key={selectedPropertyId}
                searchable={true}
                hideOptional
                loading={isPropertyDataLoading}
                disabled={Boolean(unitName) || isSingleFamily}
                value={unitValue}
                menuPortalTarget={document.body}
                menuPosition="absolute"
                menuPlacement="bottom"
                options={unitOptions}
                // @ts-expect-error
                loadOptions={loadUnitOptions}
                additional={{ page: 1, propertyId: selectedPropertyId }}
                onChange={(e) => {
                  if (!Array.isArray(e))
                    setSelectedUnitId(Number(e.target.value));
                }}
              />
            </div>
          </div>
          <Show if={!!unit && !applicationId && !isLoading}>
            <SetRentAmountOptions
              unit={unit}
              manualRent={manualRent}
              setManualRent={setManualRent}
              useListedRent={useListedRent}
              setUseListedRent={setUseListedRent}
              selectedTemplate={selectedTemplate}
            />
          </Show>
        </div>

        <Show if={selectedPropertyId}>
          <div className="p-6 border-t border-alpha-40">
            <TemplateSelector
              propertyId={selectedPropertyId}
              defaultTemplateId={propertyData?.verification_template?.id}
              onTemplateChange={handleTemplateChange}
            />
          </div>
        </Show>
      </div>

      <div className="flex justify-between px-6 py-4 mt-auto border-t border-alpha-40">
        <Button
          variant={ButtonVariant.TEXT}
          size={ButtonSize.XS}
          HugeIcon={DiscountTag02Icon}
          disabled={!availableDiscounts.length}
          className="flex items-center gap-2 text-primary-500 hover:text-primary-600"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowActiveDiscountCodes(true);
          }}
        >
          View active discount codes
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={isDisabledSubmit}
          tooltip={
            selectedPropertyId && selectedUnitId
              ? undefined
              : !selectedPropertyId
                ? `Please select a ${dictionary.property}`
                : `Please select a ${dictionary.unit}`
          }
          tooltipZIndex={50}
          onClick={() => setStepIdx(1)}
        >
          Continue
        </Button>
      </div>
      <ActiveDiscountsModal
        isOpen={showActiveDiscountCodes}
        onClose={() => setShowActiveDiscountCodes(false)}
        discounts={availableDiscounts}
      />
    </div>
  );
};
